import React, { useState, useEffect } from 'react';
import { projectsTaskTrackerApi, type TaskTrackerColumnDto, type TaskTrackerItemDto } from '../../services/api';

export interface PlanningItemOption {
  id: string;
  name: string;
}

interface AddTaskItemModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (item: TaskTrackerItemDto) => void;
  sprintId: string;
  columns: TaskTrackerColumnDto[];
  defaultColumnId: string;
  planningItems: PlanningItemOption[];
  projectId: string;
  editingItem?: TaskTrackerItemDto | null;
  onUpdate?: (item: TaskTrackerItemDto) => void;
  onMoveItemToColumn?: (itemId: string, columnId: string) => void | Promise<void>;
  onDeleteItem?: (itemId: string) => void | Promise<void>;
}

export default function AddTaskItemModal({
  open,
  onClose,
  onSuccess,
  sprintId,
  columns,
  defaultColumnId,
  planningItems,
  projectId,
  editingItem = null,
  onUpdate,
  onMoveItemToColumn,
}: AddTaskItemModalProps) {
  const [title, setTitle] = useState('');
  const [selectedPlanningItemId, setSelectedPlanningItemId] = useState('');
  const [description, setDescription] = useState('');
  const [columnId, setColumnId] = useState(defaultColumnId);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const usePlanningDropdown = planningItems.length > 0;
  const resolvedTitle = usePlanningDropdown && !editingItem
    ? (selectedPlanningItemId ? planningItems.find((p) => p.id === selectedPlanningItemId)?.name ?? '' : '')
    : title.trim();

  useEffect(() => {
    if (open) {
      if (editingItem) {
        setTitle(editingItem.title);
        const match = planningItems.find((p) => p.name === editingItem.title || p.id === editingItem.planning_item_id);
        setSelectedPlanningItemId(match?.id ?? '');
        setDescription(editingItem.description || '');
        setColumnId(editingItem.column_id);
      } else {
        setTitle('');
        setSelectedPlanningItemId(planningItems[0]?.id ?? '');
        setDescription('');
        setColumnId(defaultColumnId);
      }
      setError(null);
    }
  }, [open, editingItem, defaultColumnId, planningItems]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = usePlanningDropdown && !editingItem ? resolvedTitle : title.trim();
    if (!trimmed) {
      setError(usePlanningDropdown && !editingItem ? 'Please select a planning item as the task title.' : 'Title is required.');
      return;
    }
    if (!projectId) return;
    setSaving(true);
    setError(null);
    try {
      if (editingItem && onUpdate) {
        const res = await projectsTaskTrackerApi.updateItem(projectId, editingItem.id, {
          title: trimmed,
          description: description.trim() || null,
          column_id: columnId !== editingItem.column_id ? columnId : undefined,
          planning_item_id: usePlanningDropdown ? selectedPlanningItemId || null : undefined,
        });
        if (res.data) {
          onUpdate(res.data);
          onClose();
        }
        return;
      }
      const planningItemId = usePlanningDropdown ? selectedPlanningItemId || null : null;
      const res = await projectsTaskTrackerApi.createItem(projectId, {
        sprint_id: sprintId,
        column_id: columnId,
        title: trimmed,
        description: description.trim() || null,
        planning_item_id: planningItemId,
      });
      if (res.data) {
        onSuccess(res.data);
        onClose();
      } else if (res.message) {
        setError(res.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const isEdit = !!editingItem;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Task' : 'Add Task'}</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEdit ? 'Update the task details.' : 'Add a task to the sprint. Choose the column (e.g. Todo).'}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            {usePlanningDropdown && !editingItem ? (
              <select
                value={selectedPlanningItemId}
                onChange={(e) => setSelectedPlanningItemId(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
              >
                <option value="">— Select a planning item —</option>
                {planningItems.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                placeholder={planningItems.length === 0 ? 'Task title (add planning items in Project Planning)' : 'Task title'}
              />
            )}
            {!editingItem && planningItems.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">Add planning items in Project Planning to choose from a dropdown.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none resize-none"
              placeholder="Optional details"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Column</label>
            <select
              value={columnId}
              onChange={(e) => setColumnId(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
            >
              {columns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 text-sm font-semibold focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (isEdit ? 'Save' : 'Add Task')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
